// PROV-01 · R92 · ENTRADA COMPLEMENTARIA: Cuenta App -> sus Personas + administración de identidad.
// Las lecturas son paginadas por keyset en el servidor («Cargar más»): esta vista nunca descarga el
// catálogo completo ni presenta una página como el total.
import { useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import { notifyOk, notifyError } from "@/lib/notify";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  MODAL, OUTLINE, PRIMARIO, AZUL, LIMITE, rolLabel, buscarCuentas, buscarPersonas, detalleCuenta,
  errorDe, usePaginado, CargarMas, GrantEstadoBadges, ConfirmAccesoApp, CuentaAppModal, GrantModal,
  ResetPasswordAppModal,
} from "@/components/admin/accesoAppShared";import { Plus, Smartphone, Search, KeyRound } from "lucide-react";

// Selector de Persona: resultados paginados del servidor, SIN auto-selección. Escribir no habilita
// otorgar: hay que elegir explícitamente una Persona de la lista.
function PersonaPicker({ open, onOpenChange, onElegida }) {
  const [q, setQ] = useState("");
  const [sel, setSel] = useState(null);
  const pag = usePaginado((extra) => buscarPersonas({ termino: q.trim().length >= 2 ? q.trim() : null, ...extra }));
  const timer = useRef(null);

  useEffect(() => {
    if (!open) { setQ(""); setSel(null); return; }
    pag.pedir({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open) return;
    clearTimeout(timer.current);
    timer.current = setTimeout(() => { setSel(null); pag.pedir({}); }, 300);
    return () => clearTimeout(timer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={MODAL} data-testid="acceso-app-modal-persona-picker">
        <DialogHeader>
          <DialogTitle className="text-[#012472]">Elegir persona protegida</DialogTitle>
          <DialogDescription className="text-xs text-slate-500">
            Sólo aparecen personas con sitio y organización operativos. Elige una de la lista.
          </DialogDescription>
        </DialogHeader>
        <div className="relative">
          <Search strokeWidth={1.5} className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} className="pl-10 rounded-xl"
            placeholder="Buscar por nombre…" data-testid="acceso-app-persona-buscar" />
        </div>
        <div className="space-y-2 max-h-72 overflow-y-auto">
          {pag.items.length === 0 && !pag.cargando && (
            <p className="text-sm text-slate-400" data-testid="acceso-app-persona-sin-resultados">
              Ninguna persona coincide con la búsqueda.
            </p>
          )}
          {pag.items.map((p) => (
            <button key={p.persona_id} type="button" onClick={() => setSel(p)}
              data-testid={`acceso-app-persona-opcion-${p.persona_id}`}
              className={`w-full text-left rounded-xl border px-3.5 py-2.5 transition-colors ${sel?.persona_id === p.persona_id
                ? "border-[#01CBE3] bg-[#01CBE3]/10" : "border-gray-100 hover:bg-slate-50"}`}>
              <p className="text-sm font-medium text-[#012472] break-words">{p.nombre_completo}</p>
              <p className="text-xs text-slate-500 break-words">
                {p.sitio_nombre}{p.organization_nombre ? ` · ${p.organization_nombre}` : ""}
                {p.habitacion ? ` · ${p.habitacion}` : ""} · ref {p.identificador_corto}
              </p>
            </button>
          ))}
          <CargarMas hayMas={pag.hayMas} cargando={pag.cargando}
            testId="acceso-app-persona-cargar-mas"
            onClick={() => pag.pedir({}, true)} />
        </div>
        <DialogFooter className="gap-2 sticky bottom-0 bg-background pt-3 -mb-1">
          <Button variant="outline" className={OUTLINE} onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button disabled={!sel} className={PRIMARIO} data-testid="acceso-app-persona-continuar"
            onClick={() => { onElegida(sel); onOpenChange(false); }}>Continuar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function AccesoApp() {
  const [q, setQ] = useState("");
  const [sel, setSel] = useState(null);
  const [detalle, setDetalle] = useState(null);
  const [openCuenta, setOpenCuenta] = useState(false);
  const [openPicker, setOpenPicker] = useState(false);
  const [openReset, setOpenReset] = useState(false);
  const [grantCtx, setGrantCtx] = useState(null);   // { cuenta, persona, grant }
  const [confirmar, setConfirmar] = useState(null); // { tipo, grant }
  const pag = usePaginado((extra) => buscarCuentas({ termino: q.trim().length >= 2 ? q.trim() : null, ...extra }));
  const timer = useRef(null);
  const detToken = useRef(0);

  useEffect(() => { pag.pedir({}); /* eslint-disable-next-line */ }, []);
  useEffect(() => {
    clearTimeout(timer.current);
    timer.current = setTimeout(() => pag.pedir({}), 300);
    return () => clearTimeout(timer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  const cargarDetalle = async (id) => {
    const mio = ++detToken.current;
    if (!id) { setDetalle(null); return; }
    try {
      const r = await detalleCuenta(id);
      if (mio === detToken.current) setDetalle(r.data);
    } catch (e) {
      if (mio === detToken.current) { setDetalle(null); notifyError(errorDe(e)); }
    }
  };
  useEffect(() => { cargarDetalle(sel); /* eslint-disable-next-line */ }, [sel]);

  const refrescar = async () => { await Promise.all([pag.pedir({}), cargarDetalle(sel)]); };

  const cuenta = detalle?.cuenta || null;
  const grants = useMemo(() => detalle?.grants || [], [detalle]);
  const vigentes = grants.filter((g) => g.vigente).length;

  const revocar = async (gid) => {
    try {
      await api.post(`/app-admin/grants/${gid}/revocar`);
      notifyOk("Acceso revocado. La cuenta conserva sus otros accesos.");
      await refrescar();
      return true;
    } catch (e) { notifyError(errorDe(e)); return false; }
  };

  const toggleCuenta = async () => {
    try {
      await api.post(`/users/${cuenta.user_id}/${cuenta.activa ? "desactivar" : "activar"}`);
      notifyOk(cuenta.activa ? "Cuenta desactivada." : "Cuenta activada.");
      await refrescar();
      return true;
    } catch (e) { notifyError(errorDe(e)); return false; }
  };

  const conf = confirmar || {};
  const personasDelGrant = conf.grant ? [conf.grant.persona_nombre || conf.grant.persona_id] : [];

  return (
    <div className="tema-noche-auto space-y-5" data-testid="acceso-app-panel">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <p className="text-sm text-slate-500 max-w-xl">
          Cuentas de la App Residencial y los accesos explícitos que cada una tiene por persona.
          Un acceso revocado deja de autorizar de inmediato; la cuenta conserva los demás.
        </p>
        <Button data-testid="acceso-app-nueva-cuenta" onClick={() => setOpenCuenta(true)}
          className={`${PRIMARIO} shrink-0`}>
          <Plus strokeWidth={1.75} className="h-4 w-4 mr-1.5" /> Nueva cuenta App
        </Button>
      </div>

      <div className="grid lg:grid-cols-[minmax(0,360px)_1fr] gap-5 items-start">
        <div className="space-y-3">
          <div className="relative">
            <Search strokeWidth={1.5} className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} className="pl-10 rounded-xl bg-white"
              placeholder="Buscar por correo o nombre…" data-testid="acceso-app-buscar-cuenta" />
          </div>
          <div className="bg-white rounded-2xl border border-gray-100 divide-y divide-gray-100 overflow-hidden">
            {pag.items.length === 0 && !pag.cargando && (
              <p className="px-5 py-8 text-sm text-slate-400" data-testid="acceso-app-sin-cuentas">
                {q.trim() ? "Ninguna cuenta coincide con la búsqueda." : "Todavía no hay cuentas de App."}
              </p>
            )}
            {pag.items.map((c) => (
              <button key={c.user_id} type="button" onClick={() => setSel(c.user_id)}
                data-testid={`acceso-app-cuenta-${c.user_id}`}
                className={`w-full text-left px-5 py-3.5 transition-colors ${sel === c.user_id ? "bg-slate-50" : "hover:bg-slate-50/70"}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-medium text-[#012472] truncate">{c.nombre || c.email}</p>
                    <p className="text-xs text-slate-500 break-all">{c.email}</p>
                  </div>
                  <span className={`text-xs rounded-full px-2.5 py-1 shrink-0 ${c.activa ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                    {c.activa ? "Cuenta activa" : "Cuenta inactiva"}
                  </span>
                </div>
                <p className="text-xs text-slate-400 mt-1">
                  {rolLabel(c.role)} ·{" "}
                  <span data-testid={`acceso-app-grants-activos-${c.user_id}`}>
                    {(sel === c.user_id && detalle ? vigentes : c.grants_activos)} acceso
                    {(sel === c.user_id && detalle ? vigentes : c.grants_activos) === 1 ? "" : "s"} vigente
                    {(sel === c.user_id && detalle ? vigentes : c.grants_activos) === 1 ? "" : "s"}
                  </span>
                </p>
              </button>
            ))}
          </div>
          <div className="flex items-center justify-between gap-3">
            <p className="text-xs text-slate-400" data-testid="acceso-app-pagina-info">
              {pag.items.length} cuenta{pag.items.length === 1 ? "" : "s"} mostrada
              {pag.items.length === 1 ? "" : "s"}{pag.hayMas ? " · hay más" : ""}
            </p>
            <CargarMas hayMas={pag.hayMas} cargando={pag.cargando} testId="acceso-app-cargar-mas"
              onClick={() => pag.pedir({}, true)} />
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-gray-100 p-5 space-y-4">
          {!cuenta && (
            <div className="flex items-center gap-3 text-slate-400 text-sm" data-testid="acceso-app-vacio">
              <Smartphone strokeWidth={1.5} className="h-5 w-5" />
              Elige una cuenta para ver y administrar sus accesos.
            </div>
          )}
          {cuenta && (
            <>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-medium text-[#012472] break-all" data-testid="acceso-app-detalle-email">
                    {cuenta.email}
                  </p>
                  <p className="text-xs text-slate-500">
                    {cuenta.nombre} · {rolLabel(cuenta.role)} ·{" "}
                    <span data-testid="acceso-app-detalle-estado">
                      {cuenta.activa ? "Cuenta activa" : "Cuenta inactiva"}
                    </span>
                  </p>
                  {!cuenta.activa && (
                    <p className="text-xs text-amber-700 mt-1" data-testid="acceso-app-aviso-inactiva">
                      La cuenta está inactiva: no puede iniciar sesión en la App hasta reactivarla.
                    </p>
                  )}
                </div>
                <div className="flex gap-2 flex-wrap">
                  <Button variant="outline" size="sm" className={OUTLINE}
                    data-testid="acceso-app-reset-password" onClick={() => setOpenReset(true)}>
                    <KeyRound strokeWidth={1.5} className="h-4 w-4 mr-1.5" /> Restablecer contraseña
                  </Button>
                  <Button variant="outline" size="sm" className={OUTLINE}
                    data-testid="acceso-app-toggle-cuenta"
                    onClick={() => setConfirmar({ tipo: cuenta.activa ? "desactivar" : "activar" })}>
                    {cuenta.activa ? "Desactivar cuenta" : "Activar cuenta"}
                  </Button>
                  <Button size="sm" className={AZUL} data-testid="acceso-app-asignar-persona"
                    onClick={() => setOpenPicker(true)}>Asignar persona</Button>
                </div>
              </div>

              <div className="space-y-3">
                {grants.length === 0 && (
                  <p className="text-sm text-slate-400" data-testid="acceso-app-sin-grants">
                    Esta cuenta no tiene ninguna persona autorizada.
                  </p>
                )}
                {grants.map((g) => (
                  <div key={g.grant_id} data-testid={`acceso-app-grant-${g.grant_id}`}
                    className="rounded-xl border border-gray-100 p-4 space-y-2">
                    <div className="flex flex-wrap items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="font-medium text-[#012472] break-words">
                          {g.persona_nombre || g.persona_id}
                        </p>
                        <p className="text-xs text-slate-500">
                          {g.sitio_nombre} · {g.tipo === "propio" ? "Acceso propio" : "Acceso familiar"}
                          {g.relacion ? ` · ${g.relacion}` : ""}
                        </p>
                      </div>
                      <GrantEstadoBadges grant={g}
                        testId={`acceso-app-grant-estado-${g.grant_id}`} />
                    </div>
                    <p className="text-xs text-slate-400">
                      Permisos: {(g.permisos || []).join(", ") || "—"}
                      {g.vigencia_hasta ? ` · Hasta ${g.vigencia_hasta.slice(0, 10)}` : " · Sin fecha límite"}
                    </p>
                    {g.estado === "activo" && (
                      <div className="flex gap-2 pt-1">
                        <Button variant="outline" size="sm" className={OUTLINE}
                          data-testid={`acceso-app-editar-grant-${g.grant_id}`}
                          onClick={() => setGrantCtx({ cuenta, grant: g, persona: null })}>Editar</Button>
                        <Button variant="ghost" size="sm"
                          className="rounded-xl text-rose-700 hover:bg-rose-50 hover:text-rose-800 focus-visible:ring-2 focus-visible:ring-rose-300"
                          data-testid={`acceso-app-revocar-grant-${g.grant_id}`}
                          onClick={() => setConfirmar({ tipo: "revocar", grant: g })}>Revocar acceso</Button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      <CuentaAppModal open={openCuenta} onOpenChange={setOpenCuenta}
        onCreada={async (c) => { await pag.pedir({}); setSel(c.user_id); }} />

      {cuenta && (
        <PersonaPicker open={openPicker} onOpenChange={setOpenPicker}
          onElegida={(p) => setGrantCtx({ cuenta, persona: p, grant: null })} />
      )}

      <GrantModal open={!!grantCtx} onOpenChange={(v) => { if (!v) setGrantCtx(null); }}
        cuenta={grantCtx?.cuenta} persona={grantCtx?.persona} grant={grantCtx?.grant}
        onGuardado={refrescar} />

      {cuenta && (
        <ResetPasswordAppModal open={openReset} onOpenChange={setOpenReset} cuenta={cuenta}
          onHecho={refrescar} />
      )}

      <ConfirmAccesoApp
        open={conf.tipo === "revocar"} onOpenChange={(v) => { if (!v) setConfirmar(null); }}
        titulo="Revocar este acceso" testId="acceso-app-confirm-revocar" destructivo
        confirmar="Revocar acceso"
        consecuencia="Retira UN acceso, no la cuenta. La sesión activa de esa cuenta se cierra y deberá entrar de nuevo."
        lineas={[
          `Cuenta App: ${cuenta?.email || ""}`,
          `Persona afectada: ${personasDelGrant[0] || ""}`,
          "Los demás accesos de esta cuenta se conservan.",
        ]}
        onConfirm={() => revocar(conf.grant.grant_id)} />

      <ConfirmAccesoApp
        open={conf.tipo === "desactivar"} onOpenChange={(v) => { if (!v) setConfirmar(null); }}
        titulo="Desactivar esta cuenta App" testId="acceso-app-confirm-desactivar" destructivo
        confirmar="Desactivar cuenta"
        consecuencia="La cuenta deja de poder entrar a la App. NO desactiva al residente ni su monitoreo, y no borra sus accesos."
        lineas={[
          `Cuenta App: ${cuenta?.email || ""}`,
          `Personas con acceso vigente hoy: ${vigentes}`,
          grants.length > 0
            ? `Personas listadas en esta cuenta: ${grants.map((g) => g.persona_nombre || g.persona_id).join(", ")}`
            : "Esta cuenta no tiene personas autorizadas.",
        ]}
        onConfirm={toggleCuenta} />

      <ConfirmAccesoApp
        open={conf.tipo === "activar"} onOpenChange={(v) => { if (!v) setConfirmar(null); }}
        titulo="Activar esta cuenta App" testId="acceso-app-confirm-activar" confirmar="Activar cuenta"
        consecuencia="Vuelve a permitir el inicio de sesión. NO restaura accesos revocados, vencidos ni inválidos."
        lineas={[
          `Cuenta App: ${cuenta?.email || ""}`,
          `Accesos vigentes que tiene esta cuenta: ${vigentes}`,
        ]}
        onConfirm={toggleCuenta} />
    </div>
  );
}
